import React from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

export interface PageHeaderNavigationProps {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  onBack?: () => void;
  backLabel?: string;
}

/**
 * PageHeaderNavigation — canonical page header for all application screens.
 *
 * Layout: [Back] [Title + Subtitle]  ——  [Actions]
 *
 * Use this component on every page that needs a header.
 * It enforces consistent back navigation, title typography,
 * and action button placement across the entire application.
 */
const PageHeaderNavigation: React.FC<PageHeaderNavigationProps> = ({
  title,
  subtitle,
  actions,
  onBack,
  backLabel = 'Back',
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 2,
      flexShrink: 0,
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
      {onBack && (
        <Tooltip title={backLabel}>
          <IconButton size="small" onClick={onBack} sx={{ flexShrink: 0 }}>
            <ArrowBackIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="h5" noWrap>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" noWrap>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    {actions && (
      <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, alignItems: 'center' }}>
        {actions}
      </Box>
    )}
  </Box>
);

export default PageHeaderNavigation;
